// Deleting, with the same version discipline as editing.
//
// A DELETE carries `If-Match` too (§06 — "honoured when supplied", and this app
// always supplies it), which means a row edited since the page loaded refuses
// to disappear. That refusal is the feature: "delete this partner" was decided
// against a screen that may no longer describe the partner, so the 409 gets a
// deliberate re-confirmation against the version that exists now rather than a
// silent retry.

import { useCallback, useState, type ReactNode } from "react";
import styles from "./forms.module.css";
import { Button } from "../design/Button.js";
import { Dialog } from "../design/Dialog.js";
import { Notice, ProblemMessage } from "../design/Feedback.js";
import { isApiProblem } from "../api/problem.js";
import type { VersionedRecord } from "./useEditor.js";

export interface DeleteDialogProps<TRecord extends VersionedRecord> {
  open: boolean;
  onClose: () => void;
  title: string;
  /** What is about to go, and what goes with it. */
  description: string;
  record: TRecord;
  remove: (id: string, version: number) => Promise<void>;
  fetchCurrent: (id: string) => Promise<TRecord>;
  onDeleted: () => void;
}

export function DeleteDialog<TRecord extends VersionedRecord>({
  open,
  onClose,
  title,
  description,
  record,
  remove,
  fetchCurrent,
  onDeleted,
}: DeleteDialogProps<TRecord>): ReactNode {
  const [failure, setFailure] = useState<unknown>(null);
  const [stale, setStale] = useState(false);
  const [busy, setBusy] = useState(false);

  const attempt = useCallback(
    async (version: number): Promise<void> => {
      setFailure(null);
      setBusy(true);
      try {
        await remove(record.id, version);
        onDeleted();
      } catch (error) {
        if (isApiProblem(error) && error.code === "crm-conflict") setStale(true);
        else setFailure(error);
      } finally {
        setBusy(false);
      }
    },
    [record.id, remove, onDeleted],
  );

  const deleteCurrentVersion = useCallback((): void => {
    void (async () => {
      setBusy(true);
      try {
        const current = await fetchCurrent(record.id);
        setStale(false);
        await attempt(current.version);
      } catch (error) {
        setFailure(error);
        setBusy(false);
      }
    })();
  }, [attempt, fetchCurrent, record.id]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      variant="alert"
      narrow
      footer={
        <>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={busy}
            onClick={() => (stale ? deleteCurrentVersion() : void attempt(record.version))}
          >
            {busy ? "Deleting…" : stale ? "Delete the current version" : "Delete"}
          </Button>
        </>
      }
    >
      <p className={styles.description}>{description}</p>

      {stale && (
        <Notice tone="warn" title="Someone edited this after you opened it.">
          It was not deleted. What you are looking at may no longer describe the record — reload it,
          or delete the version that exists now.
        </Notice>
      )}

      {failure !== null && (
        <ProblemMessage error={failure} action={`delete this ${title.toLowerCase()}`} />
      )}
    </Dialog>
  );
}
